import { z } from 'zod/v4';

/** The statuses a tracing order can carry. */
export const TRACING_ORDER_STATUSES = [
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
] as const;

export const PatchOrderParamsSchema = z.object({
  id: z.string().min(1),
});

/**
 * Every field is optional. An omitted or `null` field keeps its current value,
 * as in the legacy patch — none of them can be cleared here.
 */
export const PatchOrderBodySchema = z.object({
  orderNumber: z.string().min(1).optional(),
  status: z.enum(TRACING_ORDER_STATUSES).nullable().optional(),
  /** ISO 8601 date-time. */
  deliveryDate: z.iso.datetime({ offset: true }).nullable().optional(),
});

export type PatchOrderBody = z.infer<typeof PatchOrderBodySchema>;

/** The order's values after the patch. */
export const PatchedOrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string().nullable(),
  status: z.string().nullable(),
  /** ISO 8601, `null` when the order has none. */
  deliveryDate: z.string().nullable(),
});

export type PatchedOrder = z.infer<typeof PatchedOrderSchema>;

/** Wire shape: the standard `reply.apiSuccess` envelope. */
export const PatchOrderResponseSchema = z.object({
  metadata: z.object({ success: z.literal(true) }),
  data: PatchedOrderSchema,
});

export type PatchOrderResponse = z.infer<typeof PatchOrderResponseSchema>;

/** Error codes the patch answers with, besides `not_found`. */
export const PATCH_ORDER_ERROR_CODES = {
  /** 409 — another of the company's orders on the same side already has the number. */
  ORDER_NUMBER_TAKEN: 'order_number_already_existing',
  /** 409 — a field the Order Hub manages for a request sent from it. */
  LOCKED_BY_ORDER_HUB: 'tracing_fields_locked_by_order_hub',
} as const;
